import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { verifyCertificate, verifyCertificateByNumber } from "../../services/certificateService";
import { Certificate } from "../../types/certificate";

export default function CertificateVerification() {
  const { verificationCode } = useParams<{ verificationCode: string }>();
  const [searchParams] = useSearchParams();
  const certificateNumber = searchParams.get("number");

  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const verify = async () => {
      try {
        let cert = null;

        if (verificationCode) {
          cert = await verifyCertificate(verificationCode);
        } else if (certificateNumber) {
          cert = await verifyCertificateByNumber(certificateNumber);
        }

        if (cert) {
          setCertificate(cert);
        } else {
          setError("ไม่พบใบเซอร์ติฟิเคตที่ต้องการตรวจสอบ");
        }
      } catch (err) {
        setError("เกิดข้อผิดพลาดในการตรวจสอบ");
      } finally {
        setLoading(false);
      }
    };

    if (verificationCode || certificateNumber) {
      verify();
    } else {
      setLoading(false);
    }
  }, [verificationCode, certificateNumber]);

  const handleSearch = async () => {
    if (!searchInput.trim()) return;

    setLoading(true);
    setError("");

    try {
      const cert = await verifyCertificateByNumber(searchInput.trim());
      if (cert) {
        setCertificate(cert);
      } else {
        setError("ไม่พบใบเซอร์ติฟิเคตที่ต้องการตรวจสอบ");
      }
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการตรวจสอบ");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textLight">กำลังตรวจสอบ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/logo.png" alt="EduMi" className="w-12 h-12" />
            <span className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              EduMi
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-textDark mb-2">
            🔐 ตรวจสอบใบเซอร์ติฟิเคต
          </h1>
          <p className="text-textLight">
            ตรวจสอบความถูกต้องของใบเซอร์ติฟิเคต EduMi
          </p>
        </div>

        {/* Search Box */}
        {!certificate && !error && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <h2 className="text-xl font-bold text-textDark mb-4">
              ค้นหาด้วยเลขที่ใบเซอร์ติฟิเคต
            </h2>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="EDUMI-XXXXX-XXXXX"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors"
              />
              <button
                onClick={handleSearch}
                className="bg-gradient-primary text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all"
              >
                ตรวจสอบ
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-textDark mb-2">
              ไม่พบใบเซอร์ติฟิเคต
            </h2>
            <p className="text-textLight mb-6">{error}</p>
            <button
              onClick={() => {
                setError("");
                setCertificate(null);
                setSearchInput("");
              }}
              className="bg-gradient-primary text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all"
            >
              ค้นหาอีกครั้ง
            </button>
          </div>
        )}

        {/* Success State */}
        {certificate && (
          <div className="space-y-6">
            {/* Verification Badge */}
            <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">
                ✓ ใบเซอร์ติฟิเคตถูกต้อง
              </h2>
              <p className="text-textLight">
                ใบเซอร์ติฟิเคตนี้ออกโดย EduMi และยืนยันความถูกต้องแล้ว
              </p>
            </div>

            {/* Certificate Details */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-textDark mb-6">รายละเอียดใบเซอร์ติฟิเคต</h3>

              <div className="space-y-4">
                <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                  <span className="text-textLight">เลขที่ใบเซอร์ติฟิเคต</span>
                  <span className="font-mono font-semibold text-textDark">
                    {certificate.certificateNumber}
                  </span>
                </div>

                <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                  <span className="text-textLight">ชื่อผู้รับ</span>
                  <span className="font-semibold text-textDark">{certificate.userName}</span>
                </div>

                <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                  <span className="text-textLight">คอร์ส</span>
                  <span className="font-semibold text-textDark text-right max-w-md">
                    {certificate.courseTitle}
                  </span>
                </div>

                <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                  <span className="text-textLight">ผู้สอน</span>
                  <span className="font-semibold text-textDark">{certificate.instructorName}</span>
                </div>

                <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                  <span className="text-textLight">วันที่จบคอร์ส</span>
                  <span className="text-textDark">
                    {new Date(certificate.completionDate).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>

                <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                  <span className="text-textLight">วันที่ออกใบเซอร์ติฟิเคต</span>
                  <span className="text-textDark">
                    {new Date(certificate.issueDate).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>

                {certificate.grade && (
                  <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                    <span className="text-textLight">เกรด</span>
                    <span className="font-bold text-green-500 text-xl">{certificate.grade}</span>
                  </div>
                )}

                {certificate.skills && certificate.skills.length > 0 && (
                  <div className="pt-4">
                    <span className="text-textLight block mb-3">ทักษะที่ได้รับ</span>
                    <div className="flex flex-wrap gap-2">
                      {certificate.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="bg-gradient-to-r from-purple-100 to-pink-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="text-center">
              <button
                onClick={() => {
                  setCertificate(null);
                  setSearchInput("");
                }}
                className="text-textLight hover:text-primary font-medium transition-colors"
              >
                ตรวจสอบใบเซอร์ติฟิเคตอื่น →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
